import { Group, ScrollArea, Stack, Text, useMantineTheme } from '@mantine/core';

import { IconChevronDown } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';

import { EmptyPlaceholder } from '../../../../../shared/components/UI/EmptyPlaceholder/EmptyPlaceholder';
import { Select } from '../../../../../shared/components/UI/Select/Select';

import type { FileWithPathAndType } from '../../../../../shared/api/magellan/bidResponses/dto';
import type {
  WorkspaceDocument,
  WorkspaceDocumentType,
} from '../../../../../shared/entities/WorkspaceDocuments';
import { DocumentInput } from '../../../../documents/components/DocumentInput';
import { BlueHighlight } from '../../../../documents/components/DocumentInput';
import DocumentItem from '../../../../documents/components/DocumentItem';
import { useDocumentSidePanel } from '../../../../documents/hooks/useDocumentSidePanel';
import { DocumentStatusSelectType } from '../hooks/useDocumentStatus';
import { useDocumentStatus } from '../hooks/useDocumentStatus';
import {
  useArchivedWorkspaceDocumentsQuery,
  useCreateWorkspaceDocumentQuery,
  useUpdateWorkspaceDocumentQuery,
  useWorkspaceDocumentsQuery,
} from '../hooks/workspaceDocuments.hook';

export function WorkspaceDocumentsTab() {
  const { documentStatusSelectTypeLabel, selectedView, handleViewChange } =
    useDocumentStatus();

  return (
    <Stack
      p="04"
      sx={{
        background: 'white',
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      <Header
        documentStatusSelectTypeLabel={documentStatusSelectTypeLabel}
        selectedView={selectedView}
        handleViewChange={handleViewChange}
      />
      <WorkspaceDocuments selectedView={selectedView} />
    </Stack>
  );
}

type HeaderProps = {
  documentStatusSelectTypeLabel: { value: string; label: string }[];
  selectedView: string;
  handleViewChange: (view: DocumentStatusSelectType | null) => void;
};

function Header({
  documentStatusSelectTypeLabel,
  selectedView,
  handleViewChange,
}: HeaderProps) {
  const { t } = useTranslation('bidResponse');
  const theme = useMantineTheme();
  return (
    <Group spacing="02" position="apart">
      <Text variant="sm" fw={500} color="gray.9">
        {t('vault.workspaceDocumentsList.title')}
      </Text>
      <Select
        variant="unstyled"
        w="170px"
        data={documentStatusSelectTypeLabel}
        value={selectedView}
        onChange={handleViewChange}
        size="xs"
        clearable={false}
        rightSection={
          <IconChevronDown size={14} color={theme.colors.gray[6]} />
        }
      />
    </Group>
  );
}

function WorkspaceDocuments({
  selectedView,
}: {
  selectedView: DocumentStatusSelectType;
}) {
  const { mutate: createWorkspaceDocument } = useCreateWorkspaceDocumentQuery();
  const handleDrop = (files: FileWithPathAndType<WorkspaceDocumentType>[]) => {
    createWorkspaceDocument(files);
  };

  if (selectedView === DocumentStatusSelectType.ARCHIVED) {
    return <WorkspaceArchivedDocumentList />;
  }

  return (
    <>
      <WorkspaceActiveDocumentList />
      <DocumentInput
        onDrop={handleDrop}
        dropzoneCaption={
          <Trans
            ns="bidResponse"
            i18nKey="vault.workspaceDocumentsList.dropzoneCaption"
            components={{ highlight: <BlueHighlight /> }}
          />
        }
      />
    </>
  );
}

function WorkspaceActiveDocumentList() {
  const { t } = useTranslation('bidResponse');
  const { data: documents } = useWorkspaceDocumentsQuery();
  const { openPanel } = useDocumentSidePanel({ documents, noBackdrop: true });

  if (documents.length === 0) {
    return (
      <EmptyPlaceholder
        title={t('vault.workspaceDocumentsList.emptyPlaceholder.title')}
        subtitle={t('vault.workspaceDocumentsList.emptyPlaceholder.subtitle')}
      />
    );
  }

  return (
    <Stack spacing="02" style={{ flex: 1, overflow: 'hidden' }}>
      <ScrollArea w="100%" h="100%">
        {documents.map(document => (
          <WorkspaceActiveDocumentItem
            key={document.id + document.prettyName}
            document={document}
            openDocumentSidePanel={openPanel}
          />
        ))}
      </ScrollArea>
    </Stack>
  );
}

function WorkspaceActiveDocumentItem({
  document,
  openDocumentSidePanel,
}: {
  document: WorkspaceDocument;
  openDocumentSidePanel: (document: WorkspaceDocument) => void;
}) {
  const { mutate: updateWorkspaceDocument } = useUpdateWorkspaceDocumentQuery();

  return (
    <DocumentItem
      key={document.id}
      document={document}
      onClick={() => openDocumentSidePanel(document)}
    >
      <DocumentItem.Icon />
      <DocumentItem.TypeSelect.WorkspaceDocument
        onChange={type => {
          updateWorkspaceDocument({ ...document, type });
        }}
      />
      <DocumentItem.Name />
      <DocumentItem.Divider displayOnHover />
      <DocumentItem.PreviewButton displayOnHover />
      <DocumentItem.ArchiveButton.WorkspaceDocument displayOnHover />
      <DocumentItem.Spacer />
      <DocumentItem.DocumentDownloadButton displayOnHover />
    </DocumentItem>
  );
}

function WorkspaceArchivedDocumentList() {
  const { data: documents } = useArchivedWorkspaceDocumentsQuery();

  return (
    <Stack spacing="02">
      {documents.map(document => (
        <WorkspaceArchivedDocumentItem key={document.id} document={document} />
      ))}
    </Stack>
  );
}

function WorkspaceArchivedDocumentItem({
  document,
}: {
  document: WorkspaceDocument;
}) {
  return (
    <DocumentItem key={document.id} document={document}>
      <DocumentItem.Icon />
      <DocumentItem.Type.WorkspaceDocument />
      <DocumentItem.Name />
      <DocumentItem.Divider displayOnHover />
      <DocumentItem.Spacer />
      <DocumentItem.RestoreButton.WorkspaceDocument displayOnHover />
    </DocumentItem>
  );
}
